import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  FileText, User, Briefcase, Code, Award, Trophy, Star, 
  ExternalLink, Github, Linkedin, Globe, MapPin, Calendar,
  CheckCircle, Zap, Target, TrendingUp, Crown, Building2,
  GraduationCap, ArrowLeft, Loader, Share2, Download, Mail,
  Phone, Clock, ChevronRight, Copy
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import VerifiedBadge from '../components/VerifiedBadge';

const API = process.env.REACT_APP_BACKEND_URL;

const tierConfig = {
  Legendary: { color: 'from-yellow-400 to-amber-500', bg: 'bg-yellow-500/10', text: 'text-yellow-600', border: 'border-yellow-500/30' },
  Elite: { color: 'from-purple-500 to-indigo-600', bg: 'bg-purple-500/10', text: 'text-purple-600', border: 'border-purple-500/30' },
  Expert: { color: 'from-cyan-400 to-blue-500', bg: 'bg-cyan-500/10', text: 'text-cyan-600', border: 'border-cyan-500/30' },
  Advanced: { color: 'from-green-400 to-emerald-500', bg: 'bg-green-500/10', text: 'text-green-600', border: 'border-green-500/30' },
  Intermediate: { color: 'from-amber-400 to-orange-500', bg: 'bg-amber-500/10', text: 'text-amber-600', border: 'border-amber-500/30' },
  Beginner: { color: 'from-gray-400 to-gray-500', bg: 'bg-gray-500/10', text: 'text-gray-600', border: 'border-gray-500/30' }
};

const PublicResumePage = () => {
  const { username } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  
  useEffect(() => {
    fetchResume();
  }, [username]);
  
  const fetchResume = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/resume/${username}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Resume not found');
    } finally {
      setLoading(false);
    }
  };

  const copyProfileLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Profile link copied!');
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center">
        <div className="text-center">
          <Loader className="animate-spin text-purple-600 mx-auto mb-4" size={48} />
          <p className="text-gray-500">Loading resume...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center">
        <div className="text-center">
          <FileText size={64} className="mx-auto mb-4 text-gray-300" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Resume Not Found</h1>
          <p className="text-gray-500 mb-4">{error}</p>
          <Link to="/codearena" className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
            <ArrowLeft size={18} />
            Go to EvalentX Arena
          </Link>
        </div>
      </div>
    );
  }
  
  const { resume, student, verified_data, verified_talent, parsed_resume } = data;
  const talentScore = verified_data?.talent_score;
  const ranking = verified_data?.ranking;
  const tierName = talentScore?.tier?.name || 'Beginner';
  const tier = tierConfig[tierName] || tierConfig.Beginner;
  
  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Top Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/codearena" className="flex items-center gap-2 text-gray-700 hover:text-purple-600 transition-colors">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <Zap className="text-white" size={18} />
            </div>
            <span className="font-bold">EvalentX</span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={copyProfileLink}
              className="px-3 py-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors flex items-center gap-2 text-sm"
              data-testid="copy-link-btn"
            >
              <Copy size={16} />
              Copy Link
            </button>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-2 text-sm"
            >
              <Linkedin size={16} />
              Share
            </a>
          </div>
        </div>
      </nav>
      
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Main Resume Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-testid="resume-card">
          {/* Header Section */}
          <div className="p-8 border-b border-gray-100">
            <div className="flex flex-col md:flex-row items-start gap-6">
              {/* Avatar */}
              <div className={`w-28 h-28 bg-gradient-to-br ${tier.color} rounded-2xl flex items-center justify-center text-white text-4xl font-bold shadow-lg flex-shrink-0`}>
                {student?.full_name?.charAt(0) || 'U'}
              </div>
              
              {/* Name & Basic Info */}
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-gray-900">{student?.full_name}</h1>
                  {verified_talent?.is_verified ? (
                    <span className="inline-flex items-center gap-1" data-testid="resume-verified-badge">
                      <VerifiedBadge isVerified planCode={verified_talent.plan_code} size="lg" />
                    </span>
                  ) : resume?.is_verified && (
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium flex items-center gap-1">
                      <CheckCircle size={14} />
                      Verified
                    </span>
                  )}
                  {resume?.has_premium && (
                    <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium flex items-center gap-1">
                      <Crown size={14} />
                      Premium
                    </span>
                  )}
                </div>

                {verified_talent?.is_verified && (
                  <div className="text-xs text-gray-500 mb-3 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="inline-flex items-center gap-1">
                      <CheckCircle size={11} className="text-emerald-600" />
                      AI interview score <strong className="text-gray-800">{Math.round(verified_talent.best_score || 0)}%</strong>
                    </span>
                    {verified_talent.badge_expires_at && (
                      <span>· Badge valid until {new Date(verified_talent.badge_expires_at).toLocaleDateString()}</span>
                    )}
                    {verified_talent.plan_name && <span>· {verified_talent.plan_name} plan</span>}
                  </div>
                )}
                
                <p className="text-xl text-gray-600 mb-3">{resume?.headline || student?.department || 'Software Developer'}</p>
                
                {/* Contact Info Row */}
                <div className="flex flex-wrap items-center gap-4 text-gray-500 text-sm">
                  {student?.college && (
                    <span className="flex items-center gap-1">
                      <GraduationCap size={16} className="text-purple-500" />
                      {student.college}
                    </span>
                  )}
                  {student?.location && (
                    <span className="flex items-center gap-1">
                      <MapPin size={16} className="text-rose-500" />
                      {student.location}
                    </span>
                  )}
                  {student?.email && (
                    <a href={`mailto:${student.email}`} className="flex items-center gap-1 hover:text-purple-600">
                      <Mail size={16} className="text-blue-500" />
                      {student.email}
                    </a>
                  )}
                </div>
                
                {/* Social Links */}
                <div className="flex gap-2 mt-4">
                  {student?.github_url && (
                    <a 
                      href={student.github_url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      title="GitHub"
                    >
                      <Github size={18} />
                    </a>
                  )}
                  {student?.linkedin_url && (
                    <a 
                      href={student.linkedin_url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      title="LinkedIn"
                    >
                      <Linkedin size={18} />
                    </a>
                  )}
                  {student?.portfolio_url && (
                    <a 
                      href={student.portfolio_url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      title="Portfolio"
                    >
                      <Globe size={18} />
                    </a>
                  )}
                  {student?.resume_url && (
                    <a 
                      href={student.resume_url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="p-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                      title="Download Resume"
                    >
                      <Download size={18} />
                    </a>
                  )}
                </div>
              </div>
              
              {/* Talent Score Card */}
              <div className={`${tier.bg} ${tier.border} border rounded-2xl p-5 text-center flex-shrink-0 min-w-[140px]`}>
                <div className={`text-4xl font-bold ${tier.text}`}>{talentScore?.score || 0}</div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mt-1">CVTS Score</div>
                <div className={`mt-3 px-3 py-1 bg-gradient-to-r ${tier.color} text-white rounded-full text-sm font-medium flex items-center justify-center gap-1`}>
                  <Crown size={14} />
                  {tierName}
                </div>
                {ranking?.rank && (
                  <div className="mt-2 text-sm text-gray-500">
                    National Rank: <span className="font-semibold text-gray-700">#{ranking.rank}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-gray-100 border-b border-gray-100">
            <div className="p-5 text-center">
              <div className="text-2xl font-bold text-purple-600">{verified_data?.coding_stats?.problems_solved || 0}</div>
              <div className="text-sm text-gray-500">Problems Solved</div>
            </div>
            <div className="p-5 text-center">
              <div className="text-2xl font-bold text-cyan-600">{verified_data?.coding_stats?.contest_participations || 0}</div>
              <div className="text-sm text-gray-500">Contests Joined</div>
            </div>
            <div className="p-5 text-center">
              <div className="text-2xl font-bold text-amber-600">{verified_data?.projects?.length || 0}</div>
              <div className="text-sm text-gray-500">Projects</div>
            </div>
            <div className="p-5 text-center">
              <div className="text-2xl font-bold text-green-600">{verified_data?.achievements?.length || 0}</div>
              <div className="text-sm text-gray-500">Achievements</div>
            </div>
          </div>
          
          {/* Two Column Layout */}
          <div className="grid md:grid-cols-3 divide-x divide-gray-100">
            {/* Main Content - 2 cols */}
            <div className="md:col-span-2 p-8 space-y-8">
              {/* About Section */}
              {(resume?.summary || student?.bio) && (
                <section>
                  <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <User className="text-purple-500" size={20} />
                    Summary
                  </h2>
                  <p className="text-gray-600 leading-relaxed">{resume?.summary || student?.bio}</p>
                </section>
              )}

              {/* Uploaded Resume (extracted text + PDF link) */}
              {parsed_resume?.resume_text && (
                <section data-testid="parsed-resume-section">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <FileText className="text-indigo-500" size={20} />
                      Uploaded Resume
                      {verified_talent?.is_verified && (
                        <span className="ml-2 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs rounded-full flex items-center gap-1">
                          <CheckCircle size={12} />
                          AI-verified
                        </span>
                      )}
                    </h2>
                    {parsed_resume.pdf_url && (
                      <a
                        href={parsed_resume.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1 font-medium"
                        data-testid="resume-pdf-link"
                      >
                        <Download size={13} /> Original PDF
                      </a>
                    )}
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 max-h-96 overflow-y-auto">
                    <pre className="whitespace-pre-wrap break-words text-sm text-gray-700 leading-relaxed font-sans">
                      {parsed_resume.resume_text}
                    </pre>
                  </div>
                  <p className="text-[11px] text-gray-400 mt-2">
                    Extracted from {parsed_resume.filename || 'resume.pdf'} · {parsed_resume.text_length?.toLocaleString()} characters
                  </p>
                </section>
              )}

              {/* Education — show from student fields if no resume.education */}
              {(resume?.education?.length > 0 || student?.college || student?.university) && (
                <section>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <GraduationCap className="text-purple-500" size={20} />
                    Education
                  </h2>
                  {resume?.education?.length > 0 ? (
                    <div className="space-y-4">
                      {resume.education.map((edu, idx) => (
                        <div key={idx} className="border-l-2 border-purple-200 pl-4 py-2">
                          <h3 className="font-semibold text-gray-900">{edu.degree}</h3>
                          <p className="text-gray-600">{edu.institution}</p>
                          <p className="text-sm text-gray-400">{edu.year || `${edu.start_year} - ${edu.end_year || 'Present'}`}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="border-l-2 border-purple-200 pl-4 py-2">
                      <h3 className="font-semibold text-gray-900">
                        {student?.department ? `${student.department}` : 'Undergraduate Studies'}
                      </h3>
                      <p className="text-gray-600">{student.college || student.university}</p>
                      {student.year_of_study && (
                        <p className="text-sm text-gray-400">Year {student.year_of_study}</p>
                      )}
                    </div>
                  )}
                </section>
              )}
              
              {/* Experience Section */}
              {resume?.experience?.length > 0 && (
                <section>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Briefcase className="text-blue-500" size={20} />
                    Experience
                  </h2>
                  <div className="space-y-4">
                    {resume.experience.map((exp, idx) => (
                      <div key={idx} className="border-l-2 border-blue-200 pl-4 py-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-gray-900">{exp.title}</h3>
                            <p className="text-gray-600">{exp.company}</p>
                          </div>
                          <span className="text-sm text-gray-400 flex items-center gap-1">
                            <Calendar size={14} />
                            {exp.start_date} - {exp.end_date || 'Present'}
                          </span>
                        </div>
                        {exp.description && (
                          <p className="text-gray-500 text-sm mt-2">{exp.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}
              
              {/* Education Section (already shown above when student has no resume.education;
                  render here only if we have structured resume.education AND we also want a dedicated slot) */}
              
              {/* Projects Section */}
              {verified_data?.projects?.length > 0 && (
                <section>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Code className="text-cyan-500" size={20} />
                    Projects
                    <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-600 text-xs rounded-full flex items-center gap-1">
                      <CheckCircle size={12} />
                      Verified
                    </span>
                  </h2>
                  <div className="grid gap-4">
                    {verified_data.projects.slice(0, 4).map((project, idx) => (
                      <div key={idx} className="p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-purple-200 transition-colors">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-gray-900">{project.title}</h3>
                          <div className="flex gap-2">
                            {project.github_url && (
                              <a href={project.github_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-700">
                                <Github size={16} />
                              </a>
                            )}
                            {project.live_url && (
                              <a href={project.live_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-purple-600">
                                <ExternalLink size={16} />
                              </a>
                            )}
                          </div>
                        </div>
                        {project.description && (
                          <p className="text-gray-500 text-sm mb-3">{project.description}</p>
                        )}
                        {project.tech_stack?.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {project.tech_stack.map((tech, i) => (
                              <span key={i} className="px-2 py-0.5 bg-white border border-gray-200 text-gray-600 text-xs rounded-md">
                                {tech}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
            
            {/* Sidebar - 1 col */}
            <div className="p-8 space-y-8 bg-gray-50/50">
              {/* Skills Section */}
              {student?.skills?.length > 0 && (
                <section>
                  <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Target className="text-rose-500" size={20} />
                    Skills
                    {verified_talent?.is_verified && (
                      <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-600 text-xs rounded-full flex items-center gap-1">
                        <CheckCircle size={12} />
                        Verified
                      </span>
                    )}
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {student.skills.map((skill, idx) => (
                      <span 
                        key={idx} 
                        className="px-3 py-1.5 bg-white border border-gray-200 text-gray-700 text-sm rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {/* Domains / Focus Areas */}
              {student?.domains?.length > 0 && (
                <section>
                  <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Zap className="text-indigo-500" size={20} />
                    Focus Areas
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {student.domains.map((d, idx) => (
                      <span
                        key={idx}
                        className="px-2.5 py-1 bg-indigo-50 text-indigo-700 text-xs rounded-md border border-indigo-100"
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                </section>
              )}
              
              {/* Achievements Section */}
              {verified_data?.achievements?.length > 0 && (
                <section>
                  <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Award className="text-amber-500" size={20} />
                    Achievements
                  </h2>
                  <div className="space-y-2">
                    {verified_data.achievements.slice(0, 6).map((achievement, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                        <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Trophy className="text-white" size={18} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{achievement.name || achievement.title}</p>
                          {achievement.points && (
                            <p className="text-xs text-amber-600">+{achievement.points} pts</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
              
              {/* Looking For Section */}
              {student?.looking_for && (
                <section>
                  <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Briefcase className="text-green-500" size={20} />
                    Open To
                  </h2>
                  <div className="px-4 py-3 bg-green-100 text-green-700 rounded-lg font-medium text-center">
                    {student.looking_for}
                  </div>
                </section>
              )}
              
              {/* Coding Stats */}
              {verified_data?.coding_stats && (
                <section>
                  <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <TrendingUp className="text-cyan-500" size={20} />
                    Coding Stats
                  </h2>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-100">
                      <span className="text-gray-600 text-sm">Easy Solved</span>
                      <span className="font-semibold text-green-600">{verified_data.coding_stats.easy_solved || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-100">
                      <span className="text-gray-600 text-sm">Medium Solved</span>
                      <span className="font-semibold text-amber-600">{verified_data.coding_stats.medium_solved || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-100">
                      <span className="text-gray-600 text-sm">Hard Solved</span>
                      <span className="font-semibold text-red-600">{verified_data.coding_stats.hard_solved || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-100">
                      <span className="text-gray-600 text-sm">Total Score</span>
                      <span className="font-semibold text-purple-600">{verified_data.coding_stats.total_score || 0}</span>
                    </div>
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
        
        {/* Footer CTA */}
        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm mb-4">
            Want to build your own verified developer resume?
          </p>
          <Link 
            to="/codearena/auth" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium hover:shadow-lg hover:shadow-purple-500/25 transition-all"
          >
            Join EvalentX
            <ChevronRight size={18} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PublicResumePage;
